import { groupBy, pathOr } from 'ramda';
import { createSelector } from 'reselect';
import { IAppState } from 'spw-redux/reducer';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { INDIVIDUAL_WORKSPACES_CATEGORY } from 'spw-constants';
import { currentUser as selectCurrentUser } from '../user/selectors';
import {
  IBuilding,
  IFloor,
  IProject,
  IProjectSpaceTypeCategory,
  UserRoleInProject,
} from './types';

export const currentProject = (state: IAppState): IProject =>
  state.projectsReducer.currentProject;

export const selectProjects = (state: IAppState) =>
  state.projectsReducer.projects;

export const selectTotalFilteredProjects = (state: IAppState) =>
  state.projectsReducer.totalFilteredProjects;

export const selectCurrentProjectType = (state: IAppState) =>
  state.projectsReducer.currentProjectType;

export const selectShowNewBuildingModal = (state: IAppState) =>
  state.projectsReducer.showNewBuildingModal;

export const selectProjectDepartments = (state: IAppState) =>
  state.projectsReducer.departments;

export const selectSpaceTypeCategoriesMap = (state: IAppState) =>
  state.projectsReducer.spaceTypeCategoriesMap;

export const allCategories = (state: IAppState) =>
  state.projectsReducer.spaceTypeCategories;

export const selectProjectSpaceTypeCategories = (state: IAppState) =>
  state.projectsReducer.currentProject?.spaceTypeCategories;

export const projectSpaceTypes = (state: IAppState) =>
  state.spaceTypesReducer.spaceTypes;

export const projectColors = (state: IAppState) => state.projectsReducer.colors;

export const selectCurrentBuilding = (state: IAppState) =>
  state.projectsReducer.currentBuilding;

export const currentFloor = (state: IAppState) =>
  state.projectsReducer.currentFloor;

export const selectFloorplanImageData = (state: IAppState) =>
  state.floorplanReducer.imageData;

export const selectIsCloningProject = (state: IAppState) =>
  state.projectsReducer.cloningProjectId !== null;

export const selectPreviouslyVisitedBenchmarkProjectId = (state: IAppState) =>
  state.projectsReducer.previouslyVisitedBenchmarkProjectId;

export const selectFirstBuilding = (state: IAppState) =>
  pathOr(
    null,
    ['projectsReducer', 'currentProject', 'buildings', 0],
    state
  ) as IBuilding;

export const selectProjectSpaceTypesByCategory = createSelector(
  projectSpaceTypes,
  (spaceTypes: IProjectSpaceType[]) => {
    return groupBy((projectSpaceType: IProjectSpaceType) =>
      projectSpaceType.category.id.toString()
    )(spaceTypes);
  }
);

export const selectProjectSpaceTypesById = createSelector(
  projectSpaceTypes,
  (spaceTypes: IProjectSpaceType[]) => {
    return spaceTypes.reduce(
      (groupedSpaceTypes, spaceType: IProjectSpaceType) => {
        return {
          ...groupedSpaceTypes,
          [spaceType.id]: spaceType,
        };
      },
      {}
    );
  }
);

export const selectTotalBuildingAndFloorCount = createSelector(
  currentProject,
  (project): number[] => {
    const buildingCount = project?.buildings?.length || 0;
    const floorCount = project?.buildings?.reduce((sum, b: IBuilding) => {
      return sum + b.floors?.length;
    }, 0);
    return [buildingCount, floorCount];
  }
);

export const selectProjectHasOnlyOneBuilding = (state: IAppState) =>
  state.projectsReducer.currentProject?.buildings?.length === 1;

export const selectBuildingFloors = createSelector(
  selectFirstBuilding,
  (building: IBuilding) => {
    return building.floors?.reduce((floorsById, floor: IFloor) => {
      return {
        ...floorsById,
        [floor.id]: floor,
      };
    }, {} as { [key: number]: IFloor });
  }
);

export const selectCurrentProjectFloors = createSelector(
  currentProject,
  (
    project: IProject
  ): {
    [key: number]: IFloor & { building: IBuilding };
  } =>
    project.buildings?.reduce(
      (projectFloorsById, projectBuilding: IBuilding) => {
        const buildingFloors = projectBuilding.floors?.reduce(
          (buildingFloorsById, floor: IFloor) => {
            return {
              ...buildingFloorsById,
              [floor.id]: { ...floor, building: projectBuilding },
            };
          },
          {}
        );
        return { ...projectFloorsById, ...buildingFloors };
      },
      {}
    )
);

export const selectProjectEditorsList = (state: IAppState) =>
  state.projectsReducer.projectEditorsList;

export const selectCurrentProjectColors = (state: IAppState) =>
  state.projectsReducer.currentProjectColors;

export const selectOrgAdminJoinedCurrentProject = createSelector(
  selectProjectEditorsList,
  selectCurrentUser,
  currentProject,
  (projectEditorsList, currentUser, project) => {
    if (!project || !projectEditorsList || !currentUser) return false;
    if (project.currentUserRole !== UserRoleInProject.orgAdmin) return false;
    return !!projectEditorsList.items.find((pe) => pe.id === currentUser.id);
  }
);

export const selectCurrentUserIsViewOnly = createSelector(
  currentProject,
  (project) => project?.currentUserRole === UserRoleInProject.viewer
);

export const selectIndividualWorkspacesCategory = createSelector(
  currentProject,
  (project: IProject) => {
    return project.spaceTypeCategories.find(
      (spaceTypeCategory) =>
        spaceTypeCategory.defaultSpaceTypeCategory.category ===
        INDIVIDUAL_WORKSPACES_CATEGORY
    );
  }
);

export const selectAllProjectSpaceTypeCategories = createSelector(
  selectProjectSpaceTypeCategories,
  (projectSpaceTypeCategories: IProjectSpaceTypeCategory[]) => {
    return projectSpaceTypeCategories.reduce(
      (categories, spaceTypeCategory) => {
        if (spaceTypeCategory.subcategories) {
          return categories.concat(spaceTypeCategory.subcategories);
        }

        return [...categories, spaceTypeCategory];
      },
      []
    );
  }
);
